// The single source of truth for TapTap's gentle progress system.
// Durations are stored in milliseconds to match the existing statistics model.

export const ACHIEVEMENT_CATEGORIES = {
  FIRST_STEPS: 'firstSteps',
  TAPPING_TIME: 'tappingTime',
  TAP_COUNT: 'tapCount',
  CONSISTENCY: 'consistency',
  EXPLORATION: 'exploration',
  REFLECTION: 'reflection',
  REFLEXES: 'reflexes',
};

const achievement = (id, category, title, description, condition) => ({
  id, category, title, description, condition,
});

// The Elite Reflexes bar (also used by useStats to count/streak qualifying
// taps) — shared so the two never drift apart.
export const ELITE_REFLEX_MS = 120;

export const ACHIEVEMENTS = [
  achievement('first_tap', 'firstSteps', 'First Tap', 'Complete your first tap.', { metric: 'totalTaps', value: 1 }),
  achievement('one_minute', 'firstSteps', 'One Minute', 'Tap for a total of one minute.', { metric: 'totalActiveMs', value: 60_000 }),
  // This is also the 100-tap tier. It should only unlock once.
  achievement('first_hundred', 'firstSteps', 'First Hundred', 'Reach 100 total taps.', { metric: 'totalTaps', value: 100 }),
  achievement('finding_your_rhythm', 'firstSteps', 'Finding Your Rhythm', 'Complete your first full routine.', { metric: 'completedRoutines', value: 1 }),
  achievement('a_moment_for_you', 'firstSteps', 'A Moment for You', 'Use TapTap on three separate days.', { metric: 'distinctUseDays', value: 3 }),

  achievement('ten_minutes', 'tappingTime', 'Ten Minutes', 'Reach 10 minutes of total tapping time.', { metric: 'totalActiveMs', value: 600_000 }),
  achievement('half_an_hour', 'tappingTime', 'Half an Hour', 'Reach 30 minutes of total tapping time.', { metric: 'totalActiveMs', value: 1_800_000 }),
  achievement('one_calm_hour', 'tappingTime', 'One Calm Hour', 'Reach one hour of total tapping time.', { metric: 'totalActiveMs', value: 3_600_000 }),
  achievement('five_hours', 'tappingTime', 'Five Hours of Practice', 'Reach five hours of total tapping time.', { metric: 'totalActiveMs', value: 18_000_000 }),
  achievement('ten_hours', 'tappingTime', 'Ten Hours for You', 'Reach ten hours of total tapping time.', { metric: 'totalActiveMs', value: 36_000_000 }),
  achievement('day_of_calm', 'tappingTime', 'A Day of Calm', 'Reach 24 hours accumulated over time.', { metric: 'totalActiveMs', value: 86_400_000 }),

  achievement('building_rhythm', 'tapCount', 'Building Rhythm', 'Reach 500 total taps.', { metric: 'totalTaps', value: 500 }),
  achievement('thousand_taps', 'tapCount', 'Thousand Taps', 'Reach 1,000 total taps.', { metric: 'totalTaps', value: 1_000 }),
  achievement('steady_hands', 'tapCount', 'Steady Hands', 'Reach 5,000 total taps.', { metric: 'totalTaps', value: 5_000 }),
  achievement('ten_thousand_moments', 'tapCount', 'Ten Thousand Moments', 'Reach 10,000 total taps.', { metric: 'totalTaps', value: 10_000 }),
  achievement('deep_practice', 'tapCount', 'Deep Practice', 'Reach 25,000 total taps.', { metric: 'totalTaps', value: 25_000 }),
  achievement('halfway_hundred_thousand', 'tapCount', 'Halfway to a Hundred Thousand', 'Reach 50,000 total taps.', { metric: 'totalTaps', value: 50_000 }),
  achievement('hundred_thousand_moments', 'tapCount', 'A Hundred Thousand Moments', 'Reach 100,000 total taps.', { metric: 'totalTaps', value: 100_000 }),

  achievement('two_day_check_in', 'consistency', 'Two-Day Check-in', 'Use TapTap on two consecutive days.', { metric: 'currentStreakDays', value: 2 }),
  achievement('three_day_rhythm', 'consistency', 'Three-Day Rhythm', 'Use TapTap on three consecutive days.', { metric: 'currentStreakDays', value: 3 }),
  achievement('week_of_moments', 'consistency', 'A Week of Moments', 'Use TapTap on seven consecutive days.', { metric: 'currentStreakDays', value: 7 }),
  achievement('two_weeks_of_care', 'consistency', 'Two Weeks of Care', 'Use TapTap on 14 consecutive days.', { metric: 'currentStreakDays', value: 14 }),
  achievement('month_of_care', 'consistency', 'A Month of Care', 'Use TapTap on 30 consecutive days.', { metric: 'currentStreakDays', value: 30 }),
  achievement('season_of_steadiness', 'consistency', 'Season of Steadiness', 'Use TapTap on 90 consecutive days.', { metric: 'currentStreakDays', value: 90 }),

  achievement('try_something_new', 'exploration', 'Try Something New', 'Complete two different routines.', { metric: 'uniqueRoutinesCompleted', value: 2 }),
  achievement('explorer', 'exploration', 'Explorer', 'Complete five different routines.', { metric: 'uniqueRoutinesCompleted', value: 5 }),

  achievement('pause_and_notice', 'reflection', 'Pause and Notice', 'Complete your first optional check-in.', { metric: 'checkInsCompleted', value: 1 }),
  achievement('week_of_noticing', 'reflection', 'A Week of Noticing', 'Complete seven check-ins over time.', { metric: 'checkInsCompleted', value: 7 }),
  achievement('small_changes', 'reflection', 'Small Changes', 'Record an improvement in how you feel after a routine.', { metric: 'improvedCheckIns', value: 1 }),
  achievement('knowing_yourself', 'reflection', 'Knowing Yourself', 'Visit your personal insights page.', { metric: 'insightsViews', value: 1 }),
  achievement('come_back_gently', 'reflection', 'Come Back Gently', 'Return after a break. Your earlier progress is still here.', { metric: 'gentleReturns', value: 1 }),

  // Lower is better here, so these use `comparator: 'lte'` — see
  // getEarnedAchievements below. `baseline` anchors 0% progress on the "next
  // milestone" card; it's cosmetic only and never affects whether a badge is
  // earned. Response time can never beat the 110ms floor in useStats (100ms
  // is the accepted "impossible" line — the false-start threshold in Olympic
  // sprinting — so 110ms leaves a thin buffer above it), and 120ms for Elite
  // Reflexes sits right at genuinely elite human performance rather than
  // being an impossible target.
  achievement('quick_reflexes', 'reflexes', 'Quick Reflexes', 'Tap within 300ms of an orb appearing, at least once.', { metric: 'bestReactionMs', value: 300, comparator: 'lte', baseline: 600 }),
  achievement('sharp_focus', 'reflexes', 'Sharp Focus', 'Tap within 220ms of an orb appearing, at least once.', { metric: 'bestReactionMs', value: 220, comparator: 'lte', baseline: 600 }),
  achievement('lightning_fast', 'reflexes', 'Lightning Fast', 'Tap within 160ms of an orb appearing, at least once.', { metric: 'bestReactionMs', value: 160, comparator: 'lte', baseline: 600 }),
  achievement('elite_reflexes', 'reflexes', 'Elite Reflexes', `Tap within ${ELITE_REFLEX_MS}ms of an orb appearing — right at the edge of human reflexes.`, { metric: 'bestReactionMs', value: ELITE_REFLEX_MS, comparator: 'lte', baseline: 600 }),
  // These two count elite-speed taps themselves (a plain "bigger is better"
  // tally), not the reaction time — the default gte comparator applies.
  achievement('elite_ten_total', 'reflexes', 'Ten Elite Taps', `Land ten taps under ${ELITE_REFLEX_MS}ms, across all your sessions.`, { metric: 'eliteReflexCount', value: 10 }),
  achievement('elite_ten_row', 'reflexes', 'Ten in a Row', `Land ten taps under ${ELITE_REFLEX_MS}ms back-to-back, with nothing slower between them.`, { metric: 'bestEliteReflexStreak', value: 10 }),
];

// Shared between the Stats screen's milestone grid and the unlock toast, so a
// category always reads as the same colour wherever it appears.
export const CATEGORY_PALETTE = {
  firstSteps: ['#60a5fa', '#8b5cf6'],
  tappingTime: ['#38bdf8', '#6366f1'],
  tapCount: ['#818cf8', '#c084fc'],
  consistency: ['#67e8f9', '#8b5cf6'],
  exploration: ['#a78bfa', '#f0abfc'],
  reflection: ['#7dd3fc', '#c4b5fd'],
  reflexes: ['#fbbf24', '#f472b6'],
};

export const PROGRESS_DESIGN_RULES = Object.freeze({
  allowHideAllProgress: true,
  showLongestRhythm: true,
  preserveHistoryOnStreakReset: true,
  allowStreakPause: true,
  punitiveMessages: false,
  competitiveLeaderboards: false,
  childVisualLanguage: ['stars', 'pathways', 'gardens', 'calm creatures'],
});

export const getAchievementsForCategory = (category) =>
  ACHIEVEMENTS.filter((item) => item.category === category);

export const getEarnedAchievements = (metrics = {}) =>
  ACHIEVEMENTS.filter(({ condition }) => {
    const raw = metrics[condition.metric];
    // 'lte' metrics (lower is better, e.g. reaction time) must have an actual
    // recorded value — unlike 'gte', a missing metric is never treated as 0,
    // since 0 would trivially satisfy "at or under" a threshold.
    if (condition.comparator === 'lte') return raw != null && Number(raw) <= condition.value;
    return Number(raw ?? 0) >= condition.value;
  });
